import { useEffect, type FC, type ReactNode } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { Toaster } from 'sonner'
import { AppRoutes } from './AppRoutes'
import { AuthExpiryListener } from './components/Auth/AuthExpiryListener'
import { ErrorBoundary } from './components/ErrorBoundary'
import { usePrefetchModels } from './hooks/usePrefetchModels'
import { useAuthStore } from './stores/authStore'

/**
 * AppShell - everything between the providers and the router.
 *
 * Shared verbatim by the browser entry (`main.tsx` → `App`) and the build-time
 * prerender (`entry-ssg.tsx`). Sharing it is not tidiness, it is the hydration
 * contract: the server markup and the client's first render have to agree, and
 * the cheapest way to guarantee that is to render the same component rather
 * than two hand-kept-in-sync copies of it.
 *
 * The only structural difference between the two trees is the router
 * (`BrowserRouter` vs `StaticRouter`) and `AuthExpiryListener`, which returns
 * `null` — neither contributes markup, so the two agree by construction.
 */
export const AppShell: FC<{ children: ReactNode }> = ({ children }) => (
  <ErrorBoundary>
    <Toaster position="top-right" richColors closeButton duration={4000} />
    {children}
  </ErrorBoundary>
)

/**
 * App - Main application component with routing
 *
 * The route table itself lives in `AppRoutes.tsx` so the prerender can mount it
 * under a `StaticRouter`. The hooks below are browser-only on purpose — they
 * are effects, so they no-op under the prerender rather than needing a guard.
 */
const App: FC = () => {
  // Prefetch models on app load for instant model selector opening
  usePrefetchModels()

  // Rehydrate auth state from the refresh-token cookie on every app boot.
  // The access token lives in module memory and is lost on reload — this
  // silently re-mints it via /api/auth/refresh before any AuthGuard renders.
  const bootstrap = useAuthStore(s => s.bootstrap)
  useEffect(() => {
    void bootstrap()
  }, [bootstrap])

  return (
    <AppShell>
      <BrowserRouter>
        <AuthExpiryListener />
        <AppRoutes />
      </BrowserRouter>
    </AppShell>
  )
}

export default App
